import { useEffect, useState } from "react";
import {
  HashRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { LibraryView } from "@/components/LibraryView";
import { ClassicsView } from "@/components/ClassicsView";
import { ReaderView } from "@/components/ReaderView";
import { FoliateReaderView } from "@/components/FoliateReaderView";
import { KeyDiagnostic } from "@/components/KeyDiagnostic";
import { HighlightsView } from "@/components/HighlightsView";
import { useReaderStore } from "@/store/readerStore";
import { init as initPwa } from "@/services/pwaService";
import { maybeSeedStarterLibrary } from "@/services/starterLibraryService";
import { db } from "@/services/db";
import { didOpenFail } from "@/services/openFailureService";
import { PUBLIC_MODE } from "@/services/remoteConfig";

// HashRouter is intentional — PageVoice ships as a static bundle that may
// live under a subpath like /pagevoice/, and we don't want to require
// server rewrites for deep links.
export function App() {
  const init = useReaderStore((s) => s.init);
  const theme = useReaderStore((s) => s.reader.theme);

  // Boot persisted settings before first render of any view.
  useEffect(() => {
    void init();
    // Register the service worker for PWA / offline support.
    initPwa();
    // First-run seeding for the public deployment: if the local library is
    // empty AND no personal cloud library exists, import the bundled
    // public-domain starter books. Fire-and-forget — it can never block or
    // break startup, and it no-ops forever after the first successful run.
    void maybeSeedStarterLibrary();
  }, [init]);

  // Apply the active theme to <html> so the whole shell (library, reader
  // chrome, panels) responds — and so Tailwind's dark: variants fire when
  // theme === "dark". We also shadow it into localStorage so the inline
  // boot script in index.html can pre-paint the right background on next
  // load and avoid a flash.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.classList.remove("theme-paper", "theme-white", "theme-sepia", "theme-gray", "theme-dark");
    root.classList.add(`theme-${theme}`);
    try {
      localStorage.setItem("pv-theme", theme);
    } catch {
      /* ignore — Safari private mode etc */
    }
  }, [theme]);

  return (
    <HashRouter>
      <Routes>
        <Route path="/library" element={<LibraryView />} />
        <Route path="/classics" element={<ClassicsView />} />
        <Route path="/reader/:bookId" element={<ReaderView />} />
        <Route path="/read/:bookId" element={<FoliateReaderView />} />
        <Route path="/keytest" element={<KeyDiagnostic />} />
        <Route path="/highlights/:bookId" element={<HighlightsView />} />
        {/* Boot route: route to the most-recently-opened book if there is
            one, otherwise to the library. Critical for the home-screen
            PWA — opening the icon should drop the user back into the same
            book + page they left off on, not a list. */}
        <Route path="*" element={<BootRedirect />} />
      </Routes>
    </HashRouter>
  );
}

/** Look up the most recent book by lastOpenedAt; navigate accordingly.
 *  Defensive: any failure (Dexie missing, blob missing, schema mismatch)
 *  drops the user safely on the library — never a blank screen. */
function BootRedirect() {
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Public demo deployment always lands on the library shelf — resuming
    // into a half-read book is right for the owner's PWA, wrong for a
    // public visitor's first impression.
    if (PUBLIC_MODE) {
      setTarget("/library");
      return () => {
        cancelled = true;
      };
    }
    const fallbackTimer = window.setTimeout(() => {
      // Hard safety net: if for any reason the Dexie query hangs (open
      // request blocked, schema upgrade in flight, etc), drop to the
      // library after a second so the user never sees an indefinite
      // blank screen.
      if (!cancelled) setTarget("/library");
    }, 1200);
    (async () => {
      try {
        const recent = await db.books
          .orderBy("lastOpenedAt")
          .reverse()
          .limit(1)
          .toArray();
        if (cancelled) return;
        const candidate = recent[0];
        if (candidate?.id) {
          // Never auto-resume into a book that failed to open last time.
          // 2026-09-28: one unopenable 40 MB EPUB was the most recent book,
          // so every launch resumed straight into a dead screen and the app
          // looked completely broken. Opening it from the library still
          // works — this only governs the automatic resume.
          if (didOpenFail(candidate.id)) {
            setTarget("/library");
            return;
          }
          // Verify the blob actually exists before navigating — otherwise
          // the reader will throw on mount and show an error screen.
          const blob = await db.bookBlobs.get(candidate.id);
          if (blob) {
            setTarget(`/reader/${candidate.id}`);
          } else {
            setTarget("/library");
          }
        } else {
          setTarget("/library");
        }
      } catch (err) {
        console.warn("[pagevoice] BootRedirect Dexie query failed:", err);
        if (!cancelled) setTarget("/library");
      } finally {
        window.clearTimeout(fallbackTimer);
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  if (target === null) return null;
  return <Navigate to={target} replace />;
}
